'use client';

/**
 * 页面级数据表格：HeroUI Pro `DataGrid` + 工具栏（搜索 / 枚举过滤 / 排序 / 列显示）。
 * 列与能力来自 `trpc.meta.getMeta({ source, scene: 'table' })`（`searchable`、`filterable`、`sortable`、枚举 `options`）。
 *
 * 搜索、枚举筛选项由元数据驱动；`searchKeyword` / `enumFilters` 可与列表查询联动（受控）。
 * 枚举过滤仅渲染带 `options` 的字段，并含默认项「全部」。
 *
 * @example
 * ```tsx
 * <PageDataGrid
 *   source="users"
 *   data={users}
 *   recordCount={total}
 *   paginationBar={{}}
 *   searchKeyword={kw}
 *   onSearchKeywordChange={setKw}
 *   enumFilters={filters}
 *   onEnumFiltersChange={setFilters}
 *   sorting={sorting}
 *   onSortingChange={setSorting}
 *   pagination={pagination}
 *   onPaginationChange={setPagination}
 * >
 *   <PageDataGridHeader>...</PageDataGridHeader>
 *   <PageDataGridActionToolbar>创建 / 删除 等</PageDataGridActionToolbar>
 *   <PageDataGridActionColumn>{({ row }) => ...}</PageDataGridActionColumn>
 * </PageDataGrid>
 * ```
 */

import * as React from 'react';
import {
  Children,
  Fragment,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode, ReactElement } from 'react';
import type { Selection, SortDescriptor } from 'react-aria-components';
import type { OnChangeFn, PaginationState, Row, RowSelectionState, SortingState } from '@hs/ui';
import { cn } from '@hs/ui';
import {
  Button,
  Chip,
  Dropdown,
  Label,
  ListBox,
  Pagination,
  SearchField,
  Separator,
} from '@heroui/react';
import { DataGrid, InlineSelect } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import { Funnel, LayoutColumns3, Sliders, Xmark } from '@gravity-ui/icons';
import { trpc } from '@/lib/trpc';
import {
  isPageDataGridTableMeta,
  PAGE_DATA_GRID_ROW_ACTIONS_COLUMN_ID,
} from './page-data-grid-types';
import { buildHeroUIDataGridColumns } from './page-data-grid-heroui-columns';

const ENUM_FILTER_ALL = '__all__';

function sortingToDescriptor(sorting: SortingState): SortDescriptor | undefined {
  const s = sorting[0];
  if (!s) return undefined;
  return {
    column: s.id,
    direction: s.desc ? 'descending' : 'ascending',
  };
}

function descriptorToSorting(descriptor: SortDescriptor | undefined): SortingState {
  if (!descriptor?.column) return [];
  return [
    {
      id: String(descriptor.column),
      desc: descriptor.direction === 'descending',
    },
  ];
}

function rowSelectionToSelection<TData extends object>(
  rowSelection: RowSelectionState,
  rows: TData[],
  getRowId: (row: TData) => string
): Selection {
  const ids = rows.map((r) => getRowId(r));
  if (ids.length > 0 && ids.every((id) => rowSelection[id])) {
    return 'all';
  }
  return new Set(ids.filter((id) => rowSelection[id]));
}

function selectionToRowSelection<TData extends object>(
  sel: Selection,
  rows: TData[],
  getRowId: (row: TData) => string
): RowSelectionState {
  if (sel === 'all') {
    return Object.fromEntries(rows.map((r) => [getRowId(r), true]));
  }
  const out: RowSelectionState = {};
  for (const k of sel) {
    out[String(k)] = true;
  }
  return out;
}

export function PageDataGridHeader({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  void children;
  void className;
  return null;
}

export function PageDataGridActionColumn<TData extends object>({
  children,
}: {
  children: (ctx: { row: Row<TData> }) => React.ReactNode;
}) {
  void children;
  return null;
}

/**
 * 表格工具条右端插槽（与搜索 / 筛选 / 排序 / 列显示同一行，靠右对齐）。
 * 用于放置「创建」「批量删除」「刷新」等操作按钮。
 */
export function PageDataGridActionToolbar({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  void children;
  void className;
  return null;
}

type PartitionedSlots<TData extends object> = {
  header: { node: ReactNode; className?: string } | null;
  actionToolbar: { node: ReactNode; className?: string } | null;
  actionsRender: ((ctx: { row: Row<TData> }) => ReactNode) | null;
};

/** 展开 Fragment，保证插槽仍是 PageDataGrid 的直接子级时可被识别 */
function flattenSlotElements(children: ReactNode): ReactElement[] {
  const out: ReactElement[] = [];
  Children.forEach(children, (node) => {
    if (!isValidElement(node)) return;
    if (node.type === Fragment) {
      out.push(...flattenSlotElements((node.props as { children?: ReactNode }).children));
    } else {
      out.push(node);
    }
  });
  return out;
}

function partitionSlots<TData extends object>(
  children: ReactNode
): PartitionedSlots<TData> {
  let header: PartitionedSlots<TData>['header'] = null;
  let actionToolbar: PartitionedSlots<TData>['actionToolbar'] = null;
  let actionsRender: PartitionedSlots<TData>['actionsRender'] = null;

  for (const el of flattenSlotElements(children)) {
    const raw = el as ReactElement<{ children?: unknown; className?: string }>;
    if (raw.type === PageDataGridHeader) {
      header = { node: raw.props.children as ReactNode, className: raw.props.className };
    } else if (raw.type === PageDataGridActionToolbar) {
      actionToolbar = {
        node: raw.props.children as ReactNode,
        className: raw.props.className,
      };
    } else if (raw.type === PageDataGridActionColumn) {
      const ch = raw.props.children;
      if (typeof ch === 'function') {
        actionsRender = ch as (ctx: { row: Row<TData> }) => ReactNode;
      }
    }
  }

  return { header, actionToolbar, actionsRender };
}

export type PageDataGridPaginationBar = {
  pageSizes?: number[];
  rowsPerPageLabel?: string;
  className?: string;
};

export type PageDataGridProps<TData extends object> = {
  source: string;
  data: TData[];
  recordCount: number;
  enableSelectColumn?: boolean;
  isLoading?: boolean;
  emptyMessage?: string;
  getRowId?: (row: TData) => string;
  manualPagination?: boolean;
  pageCount?: number;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  pagination?: PaginationState;
  onPaginationChange?: OnChangeFn<PaginationState>;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  /** 按 fieldName 合并列定义（覆盖 cell、header、width 等） */
  columnOverrides?: Partial<Record<string, Partial<DataGridColumn<TData>>>>;
  allowsColumnResize?: boolean;
  metaQueryOptions?: { enabled?: boolean };
  className?: string;
  /** 搜索：与 meta `searchable` 字段配合；可与服务端查询联动 */
  searchKeyword?: string;
  onSearchKeywordChange?: (value: string) => void;
  /**
   * 枚举列过滤：fieldName → 枚举 value；空字符串表示「全部」。
   * 仅对带 `options` 的枚举字段展示下拉（并追加「全部」项）。
   */
  enumFilters?: Record<string, string>;
  onEnumFiltersChange?: (next: Record<string, string>) => void;
  paginationBar?: PageDataGridPaginationBar;
  children?: React.ReactNode;
};

const DEFAULT_PAGE_SIZE = 10;

function buildPaginationPages(totalPages: number, safePage: number): (number | 'ellipsis')[] {
  const pages: (number | 'ellipsis')[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (safePage > 3) pages.push('ellipsis');
    const start = Math.max(2, safePage - 1);
    const end = Math.min(totalPages - 1, safePage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (safePage < totalPages - 2) pages.push('ellipsis');
    pages.push(totalPages);
  }
  return pages;
}

export function PageDataGrid<TData extends object>(props: PageDataGridProps<TData>) {
  const {
    source,
    data,
    recordCount,
    enableSelectColumn = false,
    isLoading = false,
    emptyMessage = '暂无数据',
    getRowId: getRowIdProp,
    manualPagination = true,
    pageCount: pageCountProp,
    sorting: sortingControlled,
    onSortingChange,
    pagination: paginationControlled,
    onPaginationChange,
    rowSelection: rowSelectionControlled,
    onRowSelectionChange,
    columnOverrides,
    allowsColumnResize = true,
    metaQueryOptions,
    className,
    searchKeyword: searchKeywordControlled,
    onSearchKeywordChange,
    enumFilters: enumFiltersControlled,
    onEnumFiltersChange,
    paginationBar,
    children,
  } = props;

  const [sortingInner, setSortingInner] = useState<SortingState>([]);
  const [paginationInner, setPaginationInner] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [rowSelectionInner, setRowSelectionInner] = useState<RowSelectionState>({});
  const [searchInner, setSearchInner] = useState('');
  const [enumFiltersInner, setEnumFiltersInner] = useState<Record<string, string>>({});
  /** 默认「全部列」；勿用空 Set，否则首帧会只渲染 actions 列，与下一帧列数不一致触发 RAC Table 报错 */
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<Selection>('all');

  const sorting = sortingControlled ?? sortingInner;
  const setSorting = onSortingChange ?? setSortingInner;
  const pagination = paginationControlled ?? paginationInner;
  const setPagination = onPaginationChange ?? setPaginationInner;
  const rowSelection = rowSelectionControlled ?? rowSelectionInner;
  const setRowSelection = onRowSelectionChange ?? setRowSelectionInner;

  const searchKeyword =
    searchKeywordControlled !== undefined ? searchKeywordControlled : searchInner;
  const setSearchKeyword = useCallback(
    (v: string) => {
      onSearchKeywordChange?.(v);
      if (searchKeywordControlled === undefined) setSearchInner(v);
    },
    [onSearchKeywordChange, searchKeywordControlled]
  );

  const enumFilters =
    enumFiltersControlled !== undefined ? enumFiltersControlled : enumFiltersInner;
  const setEnumFilters = useCallback(
    (next: Record<string, string>) => {
      onEnumFiltersChange?.(next);
      if (enumFiltersControlled === undefined) setEnumFiltersInner(next);
    },
    [onEnumFiltersChange, enumFiltersControlled]
  );

  const { header, actionToolbar, actionsRender } = useMemo(
    () => partitionSlots<TData>(children),
    [children]
  );

  const metaQuery = trpc.meta.getMeta.useQuery(
    { source, scene: 'table' },
    { staleTime: 5 * 60 * 1000, ...metaQueryOptions }
  );

  const tableFields = useMemo(() => {
    const d = metaQuery.data;
    if (!d || !isPageDataGridTableMeta(d)) return null;
    return d.fields;
  }, [metaQuery.data]);

  const resolveRowId = useCallback(
    (row: TData) => {
      if (getRowIdProp) return getRowIdProp(row);
      const id = (row as Record<string, unknown>).id;
      return id != null ? String(id) : '';
    },
    [getRowIdProp]
  );

  const searchableFields = useMemo(
    () => (tableFields ?? []).filter((f) => f.searchable === true),
    [tableFields]
  );
  const sortableFields = useMemo(
    () => (tableFields ?? []).filter((f) => f.sortable === true),
    [tableFields]
  );
  const enumFilterFields = useMemo(
    () => (tableFields ?? []).filter((f) => f.options && f.options.length > 0),
    [tableFields]
  );

  const allDataColumnIds = useMemo(
    () => (tableFields ?? []).map((f) => f.fieldName),
    [tableFields]
  );

  /** 后端 `columnVisible: false` 时默认不展示，列仍出现在「列显示」中可勾选 */
  useEffect(() => {
    if (!tableFields?.length || allDataColumnIds.length === 0) return;
    const hidden = new Set(
      tableFields
        .filter((f) => f.columnVisible === false)
        .map((f) => f.fieldName)
    );
    if (hidden.size === 0) return;
    setVisibleColumnKeys((prev) => {
      if (prev !== 'all') return prev;
      const next = new Set(allDataColumnIds.filter((id) => !hidden.has(id)));
      return next.size > 0 ? next : 'all';
    });
  }, [tableFields, allDataColumnIds]);

  useEffect(() => {
    if (allDataColumnIds.length === 0) return;
    setVisibleColumnKeys((prev) => {
      if (prev === 'all') return 'all';
      const p = prev as Set<string>;
      const next = new Set<string>();
      for (const id of allDataColumnIds) {
        if (p.has(id)) next.add(id);
      }
      return next.size > 0 ? next : 'all';
    });
  }, [allDataColumnIds]);

  const visibleDataColumnIds = useMemo(() => {
    if (allDataColumnIds.length === 0) return new Set<string>();
    if (visibleColumnKeys === 'all') return new Set(allDataColumnIds);
    const s = visibleColumnKeys as Set<string>;
    if (s.size === 0) return new Set(allDataColumnIds);
    const next = new Set<string>();
    for (const id of allDataColumnIds) {
      if (s.has(id)) next.add(id);
    }
    return next.size > 0 ? next : new Set(allDataColumnIds);
  }, [visibleColumnKeys, allDataColumnIds]);

  /**
   * React Stately Table 行在列集合变化时可能短暂保留旧的 Cell 子节点，导致
   * 「Cell count must match column count」；列签名变化时强制 remount DataGrid。
   */
  const dataGridColumnSignature = useMemo(() => {
    const sorted = [...visibleDataColumnIds].sort().join('\u001f');
    return [
      sorted,
      actionsRender ? PAGE_DATA_GRID_ROW_ACTIONS_COLUMN_ID : '',
      enableSelectColumn ? 'sel' : '',
    ].join('\u001e');
  }, [visibleDataColumnIds, actionsRender, enableSelectColumn]);

  /** 隐藏列后若当前排序字段已不可见，清空排序，避免集合与列定义不一致 */
  useEffect(() => {
    const sortCol = sorting[0]?.id;
    if (!sortCol) return;
    if (visibleDataColumnIds.has(String(sortCol))) return;
    setSorting([]);
  }, [sorting, visibleDataColumnIds, setSorting]);

  /** 列显隐下拉用具体 key 集合，避免 `selectedKeys="all"` 与部分 Menu 实现不兼容 */
  const columnPickerSelectedKeys = useMemo((): Selection => {
    if (allDataColumnIds.length === 0) return new Set();
    if (visibleColumnKeys === 'all') return new Set(allDataColumnIds);
    const s = visibleColumnKeys as Set<string>;
    if (s.size === 0) return new Set(allDataColumnIds);
    return s;
  }, [visibleColumnKeys, allDataColumnIds]);

  const columns = useMemo(() => {
    if (!tableFields?.length) return [];

    const full = buildHeroUIDataGridColumns<TData>(tableFields, {
      columnOverrides,
      actionsRender: actionsRender
        ? (ctx) => actionsRender({ row: ctx.row as Row<TData> })
        : undefined,
      defaultAllowsResizing: allowsColumnResize,
    });

    return full.filter((c) => {
      if (c.id === PAGE_DATA_GRID_ROW_ACTIONS_COLUMN_ID) return true;
      return visibleDataColumnIds.has(String(c.id));
    });
  }, [tableFields, columnOverrides, actionsRender, visibleDataColumnIds, allowsColumnResize]);

  const firstRowHeaderId = useMemo(() => {
    const c = columns.find((col) => col.id !== PAGE_DATA_GRID_ROW_ACTIONS_COLUMN_ID);
    return c?.id;
  }, [columns]);

  const columnsWithRowHeader = useMemo(() => {
    return columns.map((c) => ({
      ...c,
      isRowHeader: c.id === firstRowHeaderId,
    }));
  }, [columns, firstRowHeaderId]);

  const sortDescriptor = useMemo(() => sortingToDescriptor(sorting), [sorting]);

  const selectedKeys = useMemo(
    () => rowSelectionToSelection(rowSelection, data, resolveRowId),
    [rowSelection, data, resolveRowId]
  );

  const handleSortChange = useCallback(
    (d: SortDescriptor) => {
      setSorting(descriptorToSorting(d));
    },
    [setSorting]
  );

  const handleSelectionChange = useCallback(
    (keys: Selection) => {
      setRowSelection(selectionToRowSelection(keys, data, resolveRowId));
    },
    [setRowSelection, data, resolveRowId]
  );

  const pageCount = useMemo(() => {
    if (pageCountProp != null) return pageCountProp;
    const ps = pagination.pageSize || DEFAULT_PAGE_SIZE;
    return Math.max(1, Math.ceil(recordCount / ps));
  }, [pageCountProp, recordCount, pagination.pageSize]);

  const safePage = Math.min(pagination.pageIndex + 1, pageCount);
  const paginationPages = useMemo(
    () => buildPaginationPages(pageCount, safePage),
    [pageCount, safePage]
  );

  const selectionCount =
    selectedKeys === 'all' ? data.length : (selectedKeys as Set<string>).size;

  const searchPlaceholder = useMemo(() => {
    if (searchableFields.length === 0) return '搜索…';
    const labels = searchableFields.map((f) => f.label).join('、');
    return `搜索${labels}`;
  }, [searchableFields]);

  const metaPending = metaQuery.isLoading || metaQuery.isFetching;
  const metaFailed = !!metaQuery.error;
  const metaInvalid =
    metaFailed ||
    (metaQuery.isFetched && !metaPending && (!tableFields || tableFields.length === 0));

  const hasMetaToolbar =
    searchableFields.length > 0 ||
    enumFilterFields.length > 0 ||
    sortableFields.length > 0 ||
    allDataColumnIds.length > 0;
  const showToolbar = hasMetaToolbar || actionToolbar != null;

  const hasActiveFilters =
    !!searchKeyword ||
    enumFilterFields.some((f) => {
      const v = enumFilters[f.fieldName];
      return v != null && v !== '' && v !== ENUM_FILTER_ALL;
    });

  const pageSizes = paginationBar?.pageSizes ?? [10, 20, 50, 100];

  return (
    <div
      className={cn(
        'flex min-h-0 w-full flex-1 flex-col p-4',
        className
      )}
    >
      {header ? (
        <header
          className={cn('mb-3 shrink-0', header.className)}
          data-slot="page-data-grid-header"
        >
          {header.node}
        </header>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col">
        {metaInvalid ? (
          <div className="text-muted-foreground flex flex-1 flex-col items-center justify-center gap-1 rounded-lg border border-dashed p-6 text-center text-sm">
            <span>无法加载「{source}」的表格元数据，请检查资源名或权限。</span>
            {metaQuery.error?.message ? (
              <span className="text-xs opacity-80">{metaQuery.error.message}</span>
            ) : null}
          </div>
        ) : metaPending && !tableFields?.length ? (
          <div className="text-muted-foreground flex flex-1 items-center justify-center text-sm">
            加载列配置…
          </div>
        ) : (
          <div
            className={cn(
              'grid min-h-0 min-w-0 w-full flex-1 gap-y-3',
              paginationBar != null
                ? 'grid-rows-[auto_minmax(0,1fr)_auto]'
                : 'grid-rows-[auto_minmax(0,1fr)]'
            )}
            data-slot="page-data-grid-main"
          >
            <div className="relative z-10 flex min-w-0 shrink-0 flex-col gap-3">
            {showToolbar ? (
              <div
                className="flex w-full min-w-0 shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2"
                data-slot="page-data-grid-toolbar"
              >
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                {searchableFields.length > 0 ? (
                  <SearchField
                    aria-label="表格搜索"
                    value={searchKeyword}
                    onChange={(v) => {
                      setSearchKeyword(v);
                      setPagination((p) => ({ ...p, pageIndex: 0 }));
                    }}
                  >
                    <SearchField.Group>
                      <SearchField.SearchIcon />
                      <SearchField.Input
                        className="w-[min(100%,220px)] min-w-[160px]"
                        placeholder={searchPlaceholder}
                      />
                      <SearchField.ClearButton />
                    </SearchField.Group>
                  </SearchField>
                ) : null}

                {enumFilterFields.map((field) => {
                  const raw = enumFilters[field.fieldName] ?? '';
                  const selected =
                    !raw || raw === ENUM_FILTER_ALL ? ENUM_FILTER_ALL : raw;
                  return (
                    <Dropdown key={field.fieldName}>
                      <Button size="sm" variant="tertiary" className="font-normal ">
                        <Funnel className="font-normal" />
                        {field.label}
                      </Button>
                      <Dropdown.Popover>
                        <Dropdown.Menu
                          selectedKeys={new Set([selected])}
                          selectionMode="single"
                          onSelectionChange={(keys) => {
                            const key = [...keys][0] as string | undefined;
                            const nextVal =
                              !key || key === ENUM_FILTER_ALL ? '' : key;
                            setEnumFilters({
                              ...enumFilters,
                              [field.fieldName]: nextVal,
                            });
                            setPagination((p) => ({ ...p, pageIndex: 0 }));
                          }}
                        >
                          <Dropdown.Item id={ENUM_FILTER_ALL} textValue="全部">
                            <Label>全部</Label>
                            <Dropdown.ItemIndicator />
                          </Dropdown.Item>
                          {(field.options ?? []).map((opt) => (
                            <Dropdown.Item
                              key={opt.value}
                              id={opt.value}
                              textValue={opt.label}
                              isDisabled={opt.disabled}
                            >
                              <Label>{opt.label}</Label>
                              <Dropdown.ItemIndicator />
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown.Popover>
                    </Dropdown>
                  );
                })}

                {sortableFields.length > 0 ? (
                  <Dropdown>
                    <Button size="sm" variant="tertiary" className="font-normal">
                      <Sliders />
                      排序
                    </Button>
                    <Dropdown.Popover>
                      <Dropdown.Menu
                        selectedKeys={
                          sortDescriptor?.column
                            ? new Set([String(sortDescriptor.column)])
                            : new Set()
                        }
                        selectionMode="single"
                        onSelectionChange={(keys) => {
                          const key = [...keys][0] as string | undefined;
                          if (!key) return;
                          const prevCol = sorting[0]?.id;
                          const prevDesc = sorting[0]?.desc;
                          const same = prevCol === key;
                          setSorting([
                            {
                              id: key,
                              desc: same ? !prevDesc : false,
                            },
                          ]);
                        }}
                      >
                        {sortableFields.map((f) => (
                          <Dropdown.Item key={f.fieldName} id={f.fieldName} textValue={f.label}>
                            <Label>{f.label}</Label>
                            <Dropdown.ItemIndicator />
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown>
                ) : null}

                {allDataColumnIds.length > 0 ? (
                  <Dropdown>
                    <Button size="sm" variant="tertiary" className="font-normal">
                      <LayoutColumns3 />
                      列显示
                    </Button>
                    <Dropdown.Popover>
                      <Dropdown.Menu
                        disallowEmptySelection
                        selectedKeys={columnPickerSelectedKeys}
                        selectionMode="multiple"
                        onSelectionChange={setVisibleColumnKeys}
                      >
                        {(tableFields ?? []).map((f) => (
                          <Dropdown.Item key={f.fieldName} id={f.fieldName} textValue={f.label}>
                            <Label>{f.label}</Label>
                            <Dropdown.ItemIndicator />
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown>
                ) : null}

                {selectionCount > 0 && enableSelectColumn ? (
                  <>
                    <Separator orientation="vertical" />
                    <span className="text-muted whitespace-nowrap text-sm">
                      已选 {selectionCount} 项
                    </span>
                  </>
                ) : null}
                </div>

                {actionToolbar ? (
                  <div
                    className={cn(
                      'flex shrink-0 flex-wrap items-center justify-end gap-2',
                      actionToolbar.className
                    )}
                    data-slot="page-data-grid-action-toolbar"
                  >
                    {actionToolbar.node}
                  </div>
                ) : null}
              </div>
            ) : null}

            {hasActiveFilters ? (
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {!!searchKeyword && searchableFields.length > 0 ? (
                  <Chip size="sm" variant="secondary">
                    <Chip.Label>搜索: {searchKeyword}</Chip.Label>
                    <button
                      type="button"
                      aria-label="清除搜索"
                      className="text-muted hover:text-foreground ml-0.5 inline-flex cursor-pointer items-center"
                      onClick={() => {
                        setSearchKeyword('');
                        setPagination((p) => ({ ...p, pageIndex: 0 }));
                      }}
                    >
                      <Xmark className="size-3" />
                    </button>
                  </Chip>
                ) : null}
                {enumFilterFields.map((f) => {
                  const v = enumFilters[f.fieldName];
                  if (!v || v === ENUM_FILTER_ALL) return null;
                  const label = f.options?.find((o) => o.value === v)?.label ?? v;
                  return (
                    <Chip key={f.fieldName} size="sm" variant="secondary">
                      <Chip.Label>
                        {f.label}: {label}
                      </Chip.Label>
                      <button
                        type="button"
                        aria-label={`清除${f.label}筛选`}
                        className="text-muted hover:text-foreground ml-0.5 inline-flex cursor-pointer items-center"
                        onClick={() => {
                          const next = { ...enumFilters, [f.fieldName]: '' };
                          setEnumFilters(next);
                        }}
                      >
                        <Xmark className="size-3" />
                      </button>
                    </Chip>
                  );
                })}
                <Button
                  size="sm"
                  variant="ghost"
                  onPress={() => {
                    setSearchKeyword('');
                    const cleared: Record<string, string> = { ...enumFilters };
                    for (const f of enumFilterFields) {
                      cleared[f.fieldName] = '';
                    }
                    setEnumFilters(cleared);
                    setPagination((p) => ({ ...p, pageIndex: 0 }));
                  }}
                >
                  清除全部
                </Button>
              </div>
            ) : null}
            </div>

            <div className="relative min-h-0 min-w-0 overflow-hidden">
              <div className="absolute inset-0 flex min-h-0 min-w-0 flex-col">
              {isLoading ? (
                <div className="bg-background/60 absolute inset-0 z-10 flex items-center justify-center text-sm">
                  加载中…
                </div>
              ) : null}
              <DataGrid<TData>
                key={dataGridColumnSignature}
                allowsColumnResize={allowsColumnResize}
                aria-label={`${source} 列表`}
                className="flex h-full min-h-0 min-w-0 flex-col"
                columns={columnsWithRowHeader}
                contentClassName="min-w-0"
                data={data}
                getRowId={(item) => resolveRowId(item)}
                renderEmptyState={() => (
                  <div className="text-muted-foreground py-8 text-center text-sm">{emptyMessage}</div>
                )}
                scrollContainerClassName={cn(
                  'h-full max-h-full min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain',
                  '[&_thead_th]:bg-[var(--surface-secondary)] [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-20',
                  /* 列参与弹性分配时须 min-w-0，否则长串不换行会溢出盖住相邻列（flex/grid 子项默认 min-width:auto） */
                  '[&_th]:min-w-0 [&_td]:min-w-0',
                  '[&_[role=columnheader]]:min-w-0 [&_[role=gridcell]]:min-w-0'
                )}
                selectedKeys={selectedKeys}
                selectionMode={enableSelectColumn ? 'multiple' : 'none'}
                showSelectionCheckboxes={enableSelectColumn}
                sortDescriptor={sortDescriptor}
                variant="primary"
                onSelectionChange={enableSelectColumn ? handleSelectionChange : undefined}
                onSortChange={sortableFields.length > 0 ? handleSortChange : undefined}
              />
              </div>
            </div>

            {paginationBar != null ? (
              <div
                className={cn(
                  'text-muted-foreground flex min-w-0 shrink-0 flex-nowrap items-center justify-between gap-3 overflow-x-auto text-sm',
                  paginationBar.className
                )}
                data-slot="page-data-grid-pagination"
              >
                <div className="flex w-fit shrink-0 items-center">
                  <Pagination className="w-fit max-w-none" size="sm">
                    <Pagination.Content className="flex w-fit min-w-0">
                      <Pagination.Item>
                        <Pagination.Previous
                        isDisabled={safePage <= 1}
                          onPress={() =>
                            setPagination((p) => ({
                              ...p,
                              pageIndex: Math.max(0, p.pageIndex - 1),
                            }))
                          }
                        >
                          <Pagination.PreviousIcon />
                        </Pagination.Previous>
                      </Pagination.Item>
                      {paginationPages.map((p, i) =>
                        p === 'ellipsis' ? (
                          <Pagination.Item key={`e-${i}`}>
                            <Pagination.Ellipsis />
                          </Pagination.Item>
                        ) : (
                          <Pagination.Item key={p}>
                            <Pagination.Link
                              isActive={p === safePage}
                              onPress={() =>
                                setPagination((prev) => ({
                                  ...prev,
                                  pageIndex: (p as number) - 1,
                                }))
                              }
                            >
                              {p}
                            </Pagination.Link>
                          </Pagination.Item>
                        )
                      )}
                      <Pagination.Item>
                        <Pagination.Next
                          isDisabled={safePage >= pageCount}
                          onPress={() =>
                            setPagination((p) => ({
                              ...p,
                              pageIndex: Math.min(pageCount - 1, p.pageIndex + 1),
                            }))
                          }
                        >
                          <Pagination.NextIcon />
                        </Pagination.Next>
                      </Pagination.Item>
                    </Pagination.Content>
                  </Pagination>
                </div>

                <div className="flex w-fit shrink-0 flex-nowrap items-center gap-3 h-4 whitespace-nowrap">
                  <InlineSelect
                    aria-label="每页条数"
                    value={String(pagination.pageSize)}
                    onChange={(v) => {
                      if (v) {
                        setPagination((p) => ({
                          ...p,
                          pageSize: Number(v),
                          pageIndex: 0,
                        }));
                      }
                    }}
                  >
                    <InlineSelect.Trigger>
                      <span className="text-muted">每页</span>
                      <InlineSelect.Value />
                      <InlineSelect.Indicator />
                    </InlineSelect.Trigger>
                    <InlineSelect.Popover className="w-[88px]">
                      <ListBox>
                        {pageSizes.map((n) => (
                          <ListBox.Item key={n} id={String(n)} textValue={String(n)}>
                            {n}
                            <ListBox.ItemIndicator />
                          </ListBox.Item>
                        ))}
                      </ListBox>
                    </InlineSelect.Popover>
                  </InlineSelect>
                  <Separator className="!h-4 shrink-0" orientation="vertical" />
                  <span className="shrink-0 text-muted">
                    第 {recordCount === 0 ? 0 : (safePage - 1) * pagination.pageSize + 1}–
                    {Math.min(safePage * pagination.pageSize, recordCount)} 条，共 {recordCount}{' '}
                    条
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

export type { PageDataGridTableFieldMeta, PageDataGridTableMeta } from './page-data-grid-types';
export { isPageDataGridTableMeta } from './page-data-grid-types';
export { buildHeroUIDataGridColumns } from './page-data-grid-heroui-columns';
